import {
  classifySportsbookTemplate,
  isFreeBetMechanic,
  resolveSportsbookTemplateSelection,
} from "helpers/sportsbookRewardMechanics";
import { templateOption, TemplateOption } from "./SportsbookTemplateDetails";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  handleUpdateFields,
  handleRemoveReward,
  getSpinsList,
} from "store/liveOps/bonuses/slice";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import CompactMultiSelectDropdown from "components/dropdowns/CompactMultiSelectDropdown/CompactMultiSelectDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";

import binIcon from "assets/icons/bin-icon.svg";
import rewardIcon from "assets/icons/reward-icon.png";
import gameImageFallback from "assets/images/image_not_found.webp";
import { getGamesListService } from "services/gamesService";
import {
  getProvidersByIdsService,
  getProvidersListService,
} from "services/providersService";
import { handleApiError } from "helpers/handleApiError";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface Props {
  currency: string;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
}

const SEARCH_PAGE_SIZE = 30;
type SportsbookTemplateRewardType = "FREEBET" | "COMBOBOOST";

const sportsbookTemplateCache = new Map<
  SportsbookTemplateRewardType,
  TemplateOption[]
>();
const sportsbookTemplateRequests = new Map<
  SportsbookTemplateRewardType,
  Promise<TemplateOption[] | null>
>();
const sportsbookTemplateGeneration = new Map<
  SportsbookTemplateRewardType,
  number
>();

export function resetSportsbookTemplateCatalog() {
  sportsbookTemplateCache.clear();
  sportsbookTemplateRequests.clear();
  sportsbookTemplateGeneration.clear();
}

async function loadSportsbookTemplates(rewardType: SportsbookTemplateRewardType) {
  const cached = sportsbookTemplateCache.get(rewardType);
  if (cached) return cached;

  const pending = sportsbookTemplateRequests.get(rewardType);
  if (pending) return pending;

  const generation = (sportsbookTemplateGeneration.get(rewardType) ?? 0) + 1;
  sportsbookTemplateGeneration.set(rewardType, generation);

  const request = (async () => {
    const options: TemplateOption[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      if (sportsbookTemplateGeneration.get(rewardType) !== generation) return null;
      const res = await instance.get(apiRoutes.bonusSportsbookTemplates, {
        params: { rewardType, page, per_page: 100 },
      });
      if (sportsbookTemplateGeneration.get(rewardType) !== generation) return null;
      options.push(
        ...res.data.items.map((item: any) =>
          templateOption(item.template_id, item.label, item.provider_template),
        ),
      );
      totalPages = res.data.total_pages;
      page += 1;
    } while (page <= totalPages);
    if (sportsbookTemplateGeneration.get(rewardType) !== generation) return null;
    sportsbookTemplateCache.set(rewardType, options);
    return options;
  })();

  sportsbookTemplateRequests.set(rewardType, request);
  const clearPending = () => {
    if (sportsbookTemplateRequests.get(rewardType) === request) {
      sportsbookTemplateRequests.delete(rewardType);
    }
  };
  request.then(clearPending, clearPending);
  return request;
}

const BonusesSettingsRewardsTab: React.FC<Props> = ({
  currency,
  fieldErrors,
  clearFieldError,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store.bonuses.payload);
  const currentItem = payload?.additionalConfigs.filter(
    (el) => el?.currency === currency,
  )[0];
  const currentFreespin = currentItem?.rewards.find(
    (el) => el.type === "freespins",
  );
  const isDepositEnabled = currentItem?.activations?.some(
    (el) => el.type === "deposit",
  );
  const optionsFull = [
    {
      value: "depositPercentage",
      label: t("depositPercentage"),
    },
    {
      value: "depositStatic",
      label: t("depositStatic"),
    },
    {
      value: "bonusBalance",
      label: t("bonusBalance"),
    },
    {
      value: "freespins",
      label: t("freespins"),
    },
    {
      value: "coins",
      label: t("coins"),
    },
    {
      value: "loyalty",
      label: t("loyalty"),
    },
    {
      value: "ranking",
      label: t("ranking"),
    },
    {
      value: "freeBets",
      label: t("sportsbookFreeBet"),
    },
    {
      value: "betRefund", label: t("betRefund"),
    },
    {
      value: "snrFreeBet", label: t("snrFreeBet"),
    },
    {
      value: "comboBoost",
      label: t("comboBoost"),
    },
  ];
  const [options, setOptions] =
    useState<{ value: string; label: string }[]>(optionsFull);

  const [freeBetTemplates, setFreeBetTemplates] = useState<TemplateOption[]>([]);
  const [freeBetTemplatesReady, setFreeBetTemplatesReady] = useState(false);

  const [comboBoostTemplates, setComboBoostTemplates] = useState<TemplateOption[]>([]);
  const [comboBoostTemplatesReady, setComboBoostTemplatesReady] = useState(false);

  const [isLoading, setIsLoading] = useState(() => {
    const gameId = currentItem?.rewards.find(
      (el) => el.type === "freespins",
    )?.gameId;

    return Boolean(isDepositEnabled && gameId);
  });

  const [isCurrencySwitching, setIsCurrencySwitching] = useState(false);

  const [searchGame, setSearchGame] = useState("");
  const [searchProvider, setSearchProvider] = useState("");
  const [selectedProviderIds, setSelectedProviderIds] = useState<
    (string | number)[]
  >([]);
  const [providersOptions, setProvidersOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providerOffset, setProviderOffset] = useState(0);
  const [hasMoreProviders, setHasMoreProviders] = useState(true);
  const [gamesOptions, setGamesOptions] = useState<
    { value: string; label: string; img?: string }[]
  >([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [gameOffset, setGameOffset] = useState(0);
  const [hasMoreGames, setHasMoreGames] = useState(true);
  const { spinsOptions, isSpinsLoading, isSpinsError } = useAppSelector(
    (state) => state.bonuses,
  );

  useEffect(() => {
    setSelectedProviderIds([]);
  }, [currency]);

  useEffect(() => {
    const providerId = currentFreespin?.providerId;
    const providerTitle = currentFreespin?.providerTitle;
    const providerSlug = currentFreespin?.providerSlug;

    if (!providerId && !providerTitle && !providerSlug) return;

    let isCurrentRequest = true;

    const loadSelectedProvider = providerId
      ? getProvidersByIdsService([Number(providerId)])
      : getProvidersListService({
          isActive: 1,
          title: providerTitle || providerSlug,
        });

    loadSelectedProvider.then((res) => {
      if (!isCurrentRequest) return;

      const provider = providerId
        ? res[0]
        : res.find(
            (item: any) =>
              item.title === providerTitle ||
              item.name === providerTitle ||
              item.slug === providerSlug,
          ) || res[0];

      if (!provider) return;

      const selectedOption = {
        value: String(provider.id),
        label: provider.title || provider.name,
      };

      setSelectedProviderIds([selectedOption.value]);
      setProvidersOptions((currentOptions) =>
        currentOptions.some((option) => option.value === selectedOption.value)
          ? currentOptions
          : [selectedOption, ...currentOptions],
      );
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [
    currentFreespin?.providerId,
    currentFreespin?.providerTitle,
    currentFreespin?.providerSlug,
  ]);

  useEffect(() => {
    let isCurrentRequest = true;

    const timeout = setTimeout(async () => {
      setLoadingProviders(true);

      try {
        const res = await getProvidersListService({
          isActive: 1,
          title: searchProvider || undefined,
          limit: SEARCH_PAGE_SIZE,
          offset: providerOffset,
        });

        if (!isCurrentRequest) return;

        const mappedProviders: { value: string; label: string }[] = res.map(
          (el: any) => ({
          value: String(el.id),
          label: el.title || el.name,
          }),
        );

        setHasMoreProviders(res.length === SEARCH_PAGE_SIZE);

        setProvidersOptions((currentOptions) => {
          const selectedOptions = currentOptions.filter((option) =>
            selectedProviderIds.some(
              (providerId) =>
                String(option.value) === String(providerId),
            ),
          );
          const nextOptions =
            providerOffset === 0
              ? [...selectedOptions, ...mappedProviders]
              : [...currentOptions, ...mappedProviders];

          return nextOptions.filter(
            (option, index, options) =>
              options.findIndex((item) => item.value === option.value) ===
              index,
          );
        });
      } finally {
        if (isCurrentRequest) setLoadingProviders(false);
      }
    }, 400);

    return () => {
      isCurrentRequest = false;
      clearTimeout(timeout);
    };
  }, [searchProvider, providerOffset]);

  useEffect(() => {
    let isCurrentRequest = true;

    const timeout = setTimeout(async () => {
      setLoadingGames(true);

      try {
        const res = await getGamesListService({
          isActive: 1,
          hasFreeSpins: 1,
          gameTitle: searchGame || undefined,
          providerIds: selectedProviderIds.length
            ? selectedProviderIds.map(Number)
            : undefined,
          limit: SEARCH_PAGE_SIZE,
          offset: gameOffset,
        });

        if (!isCurrentRequest) return;

        const mapped: { value: string; label: string; img?: string }[] = res.map((el: any) => ({
          value: String(el.id),
          label: el.title,
          img: el.image || gameImageFallback,
        }));

        setHasMoreGames(res.length === SEARCH_PAGE_SIZE);
        setGamesOptions((currentOptions) => {
          const nextOptions =
            gameOffset === 0 ? mapped : [...currentOptions, ...mapped];

          return nextOptions.filter(
            (option, index, options) =>
              options.findIndex((item) => item.value === option.value) ===
              index,
          );
        });
      } finally {
        if (isCurrentRequest) setLoadingGames(false);
      }
    }, 400);

    return () => {
      isCurrentRequest = false;
      clearTimeout(timeout);
    };
  }, [searchGame, selectedProviderIds, gameOffset]);

  const prevCurrency = useRef(currency);

  if (prevCurrency.current !== currency) {
    prevCurrency.current = currency;
    setIsCurrencySwitching(true);

    setTimeout(() => {
      setIsCurrencySwitching(false);
    }, 200);
  }

  const dispatchRef = useRef(dispatch);
  const translateRef = useRef(t);
  dispatchRef.current = dispatch;
  translateRef.current = t;

  const sportsbookTemplateRequestKey = (payload?.additionalConfigs ?? [])
    .flatMap((config) => config?.rewards ?? [])
    .reduce((types, reward) => {
      if (isFreeBetMechanic(reward?.type)) types.add("FREEBET");
      if (reward?.type === "comboBoost") types.add("COMBOBOOST");
      return types;
    }, new Set<SportsbookTemplateRewardType>());
  const sportsbookTemplateRequestKeyValue = Array.from(
    sportsbookTemplateRequestKey,
  )
    .sort()
    .join(",");

  const templateCatalog = (type?: string) =>
    type === "comboBoost" ? comboBoostTemplates : freeBetTemplates;

  const selectionFor = (
    type?: string,
    templateId?: string,
    legacyTemplateId?: string,
  ) =>
    resolveSportsbookTemplateSelection(
      templateId,
      templateCatalog(type),
      type === "comboBoost" ? comboBoostTemplatesReady : freeBetTemplatesReady,
      legacyTemplateId,
    );

  useEffect(() => {
    if (!sportsbookTemplateRequestKeyValue) return undefined;
    let active = true;
    const needed = sportsbookTemplateRequestKeyValue.split(
      ",",
    ) as SportsbookTemplateRewardType[];

    const applyTemplates = async (rewardType: SportsbookTemplateRewardType) => {
      const markReady = () => {
        if (rewardType === "FREEBET") setFreeBetTemplatesReady(true);
        else setComboBoostTemplatesReady(true);
      };
      try {
        const options = await loadSportsbookTemplates(rewardType);
        if (!active || !options) return;
        if (rewardType === "FREEBET") setFreeBetTemplates(options);
        else setComboBoostTemplates(options);
        markReady();
      } catch (error) {
        if (!active) return;
        handleApiError({
          error,
          t: translateRef.current,
          dispatch: dispatchRef.current,
        });
        markReady();
      }
    };

    needed.forEach((rewardType) => {
      applyTemplates(rewardType);
    });

    return () => {
      active = false;
    };
  }, [sportsbookTemplateRequestKeyValue]);

  useEffect(() => {
    if (!payload?.additionalConfigs || !currentItem) return;
    let changed = false;
    const rewards = currentItem.rewards.map((reward) => {
      if (!isFreeBetMechanic(reward.type) && reward.type !== "comboBoost") return reward;
      const catalog = reward.type === "comboBoost" ? comboBoostTemplates : freeBetTemplates;
      const matched = catalog.find(
        (option) => option.value === String(reward.templateId ?? ""),
      );
      if (!matched || matched.mechanic !== reward.type) return reward;
      const savedClassified =
        classifySportsbookTemplate(reward.providerTemplate) === reward.type;
      if (savedClassified && !reward.legacyTemplateId) return reward;
      changed = true;
      return {
        ...reward,
        providerTemplate: matched.providerTemplate,
        legacyTemplateId: undefined,
      };
    });
    if (!changed) return;
    dispatch(
      handleUpdateFields({
        ...payload,
        additionalConfigs: payload.additionalConfigs.map((config) =>
          config.currency === currency ? { ...config, rewards } : config,
        ),
      }),
    );
  }, [
    freeBetTemplates,
    comboBoostTemplates,
    freeBetTemplatesReady,
    comboBoostTemplatesReady,
    currency,
    payload,
    currentItem,
    dispatch,
  ]);

  useEffect(() => {
    const gameId = currentFreespin?.gameId;

    if (!gameId) return;

    dispatch(getSpinsList({ gameId: String(gameId), currency }))
      .unwrap()
      .catch((error) => {
        handleApiError({
          error,
          t,
          dispatch,
        });
      });
  }, [currentFreespin?.gameId, currency]);

  useEffect(() => {
    if (currentItem?.rewards) {
      const types = currentItem?.rewards?.map((el) => el.type);
      const monetaryTypes = [
        "depositStatic",
        "depositPercentage",
        "bonusBalance",
      ];
      const hasMonetaryReward = monetaryTypes.some((type) =>
        types.includes(type),
      );
      setOptions(
        optionsFull.filter(
          (option) =>
            !types.includes(option.value) &&
            (!hasMonetaryReward || !monetaryTypes.includes(option.value)),
        ),
      );
    } else {
      setOptions(optionsFull);
    }
  }, [currentItem]);

  useEffect(() => {
    setIsCurrencySwitching(true);

    const timer = setTimeout(() => {
      setIsCurrencySwitching(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [currency]);

  const handleDataRewards = (
    key: string,
    value: string,
    type: string,
    uid?: string,
  ) => {
    dispatch(
      handleUpdateFields({
        ...payload,
        additionalConfigs: payload?.additionalConfigs?.map((config) => {
          if (config.currency !== currency) return config;

          const types = config.rewards.map((el) => el.type);

          const enrichReward = (reward: any) => ({
            ...reward,
            [key]: value,
            ...(key === "templateId" ? {
              providerTemplate: (type === "comboBoost" ? comboBoostTemplates : freeBetTemplates)
                .find((option) => option.value === value && option.mechanic === type)?.providerTemplate,
              legacyTemplateId: undefined,
            } : {}),
            currency,
            ...(type !== "freespins" ? { gameUuid: uid } : {}),
          });

          if (!types.includes(type)) {
            return {
              ...config,
              pendingRewardDeletes:
                type === "comboBoost" || type === "bonusBalance"
                  ? (config.pendingRewardDeletes ?? []).filter(
                      (rewardType) =>
                        rewardType !==
                        (type === "comboBoost" ? "comboboost" : "bonusBalance"),
                    )
                  : config.pendingRewardDeletes,
              rewards: config.rewards
                .filter((reward) => reward.type !== type)
                .concat([
                  enrichReward(config.rewards.find((r) => r.type === type)),
                ]),
            };
          }

          const updatedRewards = config.rewards.map((reward) =>
            reward.type === type ? enrichReward(reward) : reward,
          );

          return {
            ...config,
            rewards:
              config.rewards.length > 0 ? updatedRewards : [enrichReward({})],
          };
        }),
      }),
    );
  };

  const renderInputs = (type: string) => {
    const currentReward = currentItem?.rewards.filter(
      (el) => el.type === type,
    )[0];

    const selectedGame =
      currentFreespin?.gameId && currentFreespin?.gameTitle
        ? {
            value: String(currentFreespin.gameId),
            label: currentFreespin.gameTitle,
            img: currentFreespin.gameImage || gameImageFallback,
          }
        : null;

    const finalGamesOptions = searchGame
      ? gamesOptions
      : selectedGame &&
          !gamesOptions.some((g) => g.value === selectedGame?.value)
        ? [selectedGame, ...gamesOptions]
        : gamesOptions;

    if (!currentReward) {
      return null;
    }
    if (type === "depositPercentage") {
      return (
        <div className="inputs-row">
          <div className="field field--small">
            <InnerInput
              value={currentReward.value ?? ""}
              placeholder={t("value")}
              label={t("value")}
              type="number"
              id="bets-value"
              onChange={(e) => {
                handleDataRewards("value", e, type);
                clearFieldError("depositPercentage");
              }}
              hasError={Boolean(fieldErrors?.["depositPercentage"])}
            />
          </div>
          <div className="field field--small">
            <InnerInput
              value={currentReward.max ?? ""}
              placeholder={t("maxValue")}
              label={t("maxValue")}
              type="number"
              id="bets-maxValue"
              onChange={(e) => {
                handleDataRewards("max", e, type);
                clearFieldError("depositPercentageMax");
              }}
              hasError={Boolean(fieldErrors?.["depositPercentageMax"])}
            />
          </div>
        </div>
      );
    }
    if (type === "depositStatic") {
      return (
        <div className="inputs-row">
          <InnerInput
            value={currentReward.value ?? ""}
            placeholder={t("value")}
            label={t("value")}
            type="number"
            id="bets-value"
            onChange={(e) => {
              handleDataRewards("value", e, type);
              clearFieldError("depositStatic");
            }}
            hasError={Boolean(fieldErrors?.["depositStatic"])}
          />
        </div>
      );
    }
    if (type === "bonusBalance") {
      return (
        <div className="inputs-row">
          <InnerInput
            value={currentReward.value ?? ""}
            placeholder={t("value")}
            label={t("value")}
            type="number"
            id="bonus-balance-value"
            onChange={(e) => {
              handleDataRewards("value", e, type);
              clearFieldError("bonusBalance");
            }}
            hasError={Boolean(fieldErrors?.["bonusBalance"])}
          />
        </div>
      );
    }
    if (type === "coins") {
      return (
        <div className="inputs-row">
          <div className="field field--small">
            <InnerInput
              value={currentReward.points ?? ""}
              placeholder={t("points")}
              label={t("points")}
              type="number"
              id="bets-points"
              onChange={(e) => {
                handleDataRewards("points", e, type);
                clearFieldError("coins");
              }}
              hasError={Boolean(fieldErrors?.["coins"])}
            />
          </div>
        </div>
      );
    }
    if (type === "loyalty") {
      return (
        <div className="inputs-row">
          <div className="field field--small">
            <InnerInput
              value={currentReward.points ?? ""}
              placeholder={t("points")}
              label={t("points")}
              type="number"
              id="bets-points"
              onChange={(e) => {
                handleDataRewards("points", e, type);
                clearFieldError("loyalty");
              }}
              hasError={Boolean(fieldErrors?.["loyalty"])}
            />
          </div>
        </div>
      );
    }
    if (type === "ranking") {
      return (
        <div className="inputs-row">
          <div className="field field--small">
            <InnerInput
              value={currentReward.points ?? ""}
              placeholder={t("points")}
              label={t("points")}
              type="number"
              id="bets-points"
              onChange={(e) => {
                handleDataRewards("points", e, type);
                clearFieldError("ranking");
              }}
              hasError={Boolean(fieldErrors?.["ranking"])}
            />
          </div>
        </div>
      );
    }
    if (isFreeBetMechanic(type)) {
      const currentReward = currentItem?.rewards.find(
        (el) => el.type === type,
      );
      const selection = selectionFor(
        type,
        currentReward?.templateId,
        currentReward?.legacyTemplateId,
      );

      return (
        <div className="inputs-row inputs-row--free-bet">
          <div className="field field--template">
            <DefaultDropdown
              label={t("template")}
              placeholder={t("template")}
              options={freeBetTemplates.filter((option) => option.mechanic === type)}
              selectedLabel={selection.label}
              tooltipText={selection.unavailable ? t("legacySportsbookTemplate", { id: currentReward?.templateId }) : undefined}
              isEllipsis
              useFixedMenu
              value={currentReward?.templateId || ""}
              hasError={Boolean(fieldErrors?.sportsbookTemplate)}
              isBot
              onSelect={(e) => {
                handleDataRewards("templateId", e as string, type);
                clearFieldError("sportsbookTemplate");
              }}
            />
          </div>

          <div className="field field--small">
            <InnerInput
              value={currentReward?.amount ?? ""}
              label={t("amount")}
              placeholder={t("amount")}
              type="number"
              id={`${type}-amount`}
              onChange={(e) => {
                handleDataRewards("amount", e, type);
                clearFieldError("amount");
              }}
              hasError={Boolean(fieldErrors?.["amount"])}
            />
          </div>
          <div className="field field--small">
            <InnerInput
              value={currentReward?.maxWin ?? ""}
              label={t("maxWin")}
              placeholder={t("maxWin")}
              type="number"
              id={`${type}-max-win`}
              onChange={(e) => {
                handleDataRewards("maxWin", e, type);
              }}
            />
          </div>
          <div className="field field--destination">
            <SwitchBtn
              isActive={currentReward?.winningsDestination === "real"}
              notActiveTxt={t("bonus")}
              activeTxt={t("real")}
              handleClick={() => {
                handleDataRewards(
                  "winningsDestination",
                  currentReward?.winningsDestination === "real"
                    ? "bonus"
                    : "real",
                  type,
                );
              }}
            />
          </div>
        </div>
      );
    }
    if (type === "comboBoost") {
      const currentReward = currentItem?.rewards.find(
        (el) => el.type === "comboBoost",
      );
      const selection = selectionFor(
        "comboBoost",
        currentReward?.templateId,
        currentReward?.legacyTemplateId,
      );

      return (
        <div className="inputs-row">
          <div className="field field--template">
            <DefaultDropdown
              label={t("template")}
              placeholder={t("template")}
              options={comboBoostTemplates.filter((option) => option.mechanic === "comboBoost")}
              selectedLabel={selection.label}
              isEllipsis
              useFixedMenu
              value={currentReward?.templateId || ""}
              isBot
              onSelect={(e) => {
                handleDataRewards("templateId", e as string, "comboBoost");
                clearFieldError("comboBoost");
              }}
              hasError={Boolean(fieldErrors?.["comboBoost"])}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="inputs-row inputs-row--free-spins">
        <div className="field field--provider">
          <div className="provider-multiselect">
            <div className="default-dropdown__label">
              <span>{t("provider")}</span>
            </div>
            <CompactMultiSelectDropdown
              label={t("providers")}
              items={providersOptions}
              selectedValues={selectedProviderIds}
              loading={loadingProviders}
              hasMore={hasMoreProviders}
              onSearch={(value) => {
                setProviderOffset(0);
                setSearchProvider(value);
              }}
              onLoadMore={() => {
                setProviderOffset((currentOffset) => currentOffset + 1);
              }}
              isBot
              onChange={(values) => {
                setSelectedProviderIds(values);
                setSearchGame("");
                setGamesOptions([]);
                setGameOffset(0);
                setHasMoreGames(true);

                dispatch(
                  handleUpdateFields({
                    ...payload,
                    additionalConfigs: payload?.additionalConfigs?.map(
                      (config) => {
                        if (config.currency !== currency) return config;

                        return {
                          ...config,
                          rewards: config.rewards.map((reward) =>
                            reward.type === "freespins"
                              ? {
                                  ...reward,
                                  providerId: undefined,
                                  providerTitle: undefined,
                                  providerSlug: undefined,
                                  gameId: "",
                                  gameTitle: undefined,
                                  gameImage: undefined,
                                  spinAmount: "",
                                  totalAmount: "",
                                  maxWin: "",
                                }
                              : reward,
                          ),
                        };
                      },
                    ),
                  }),
                );
              }}
            />
          </div>
        </div>
        <div className="field field--game">
          <DefaultDropdown
            placeholder={t("chooseGame")}
            label={t("game")}
            options={finalGamesOptions}
            value={currentFreespin?.gameId || ""}
            loading={loadingGames}
            search={searchGame}
            onSearch={(value) => {
              setGameOffset(0);
              setSearchGame(value);
            }}
            hasMore={hasMoreGames}
            onLoadMore={() => {
              setGameOffset((currentOffset) => currentOffset + 1);
            }}
            isBot
            onSelect={(value) => {
              dispatch(
                handleUpdateFields({
                  ...payload,
                  additionalConfigs: payload?.additionalConfigs?.map(
                    (config) => {
                      if (config.currency !== currency) return config;

                      return {
                        ...config,
                        rewards: config.rewards.map((reward) =>
                          reward.type === "freespins"
                            ? {
                                ...reward,
                                gameId: String(value),
                                spinAmount: "",
                                totalAmount: "",
                                maxWin: "",
                              }
                            : reward,
                        ),
                      };
                    },
                  ),
                }),
              );
            }}
          />
        </div>

        <div className="field field--spin">
          <DefaultDropdown
            placeholder={isSpinsError ? t("notAvailable") : t("spinAmount")}
            disabled={isSpinsError}
            label={t("spinAmount")}
            options={spinsOptions}
            value={currentFreespin?.spinAmount || ""}
            isBot
            onSelect={(e) => {
              handleDataRewards("spinAmount", e as string, "freespins");
              clearFieldError("spinAmount");
            }}
            hasError={Boolean(fieldErrors?.["spinAmount"])}
          />
        </div>
        <div className="field field--small">
          <InnerInput
            value={currentReward.totalAmount ?? ""}
            label={t("count")}
            placeholder={isSpinsError ? t("notAvailable") : t("count")}
            disabled={isSpinsError}
            type="number"
            id="bets-count"
            onChange={(e) => {
              handleDataRewards("totalAmount", e, "freespins");
              clearFieldError("totalAmount");
            }}
            hasError={Boolean(fieldErrors?.["totalAmount"])}
          />
        </div>
        <div className="field field--small">
          <InnerInput
            value={currentReward.maxWin ?? ""}
            label={t("maxWin")}
            placeholder={isSpinsError ? t("notAvailable") : t("maxWin")}
            disabled={isSpinsError}
            type="number"
            id="bets-max"
            onChange={(e) => {
              handleDataRewards("maxWin", e, "freespins");
              clearFieldError("maxWin");
            }}
            hasError={Boolean(fieldErrors?.["maxWin"])}
          />
        </div>
        <div className="field field--destination">
          <SwitchBtn
            isActive={currentReward?.winningsDestination === "real"}
            notActiveTxt={t("bonus")}
            activeTxt={t("real")}
            handleClick={() => {
              handleDataRewards(
                "winningsDestination",
                currentReward?.winningsDestination === "real"
                  ? "bonus"
                  : "real",
                "freespins",
              );
            }}
          />
        </div>
      </div>
    );
  };

  if (isCurrencySwitching) {
    return <ModalTabSkeleton />;
  }

  return (
    <div className="bonus-rewards-wrap">
      <div className="bonus-rewards-header">
        <DefaultDropdown
          placeholder={t("addReward")}
          options={options}
          isBot
          onSelect={(e) => {
            handleDataRewards("type", e as string, e as string);
          }}
        />
      </div>
      <div className="bonus-rewards-list">
        {currentItem?.rewards.map((el) => (
          <div
            className={`bonus-rewards-list--item bonus-rewards-list--item--${el.type}${isFreeBetMechanic(el.type) || el.type === "comboBoost" ? " bonus-rewards-list--item--sportsbook" : ""}`}
            key={el.type}
          >
            <div className="left">
              <img src={rewardIcon} alt="" />
              <div className="title">
                {t(
                  el.type === "freeBets"
                    ? selectionFor(el.type, el.templateId, el.legacyTemplateId).unavailable
                      ? "legacyFreeBet"
                      : "sportsbookFreeBet"
                    : el.type ?? "",
                )}
              </div>
            </div>
            <div className="right">
              {renderInputs(el.type ?? "")}
              <img
                src={binIcon}
                alt=""
                onClick={() => {
                  dispatch(
                    handleRemoveReward({
                      currency: currency,
                      type: el.type ?? "",
                    }),
                  );
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BonusesSettingsRewardsTab;
